import type { StreamedTeam } from '../types';
import { getTeamColorStyle } from '../utils/colorUtils';

interface PlayerStatsProps {
  homeTeam: StreamedTeam;
  awayTeam: StreamedTeam;
}

export function PlayerStats({ homeTeam, awayTeam }: PlayerStatsProps) {
  // Combine and sort players by points (exclude team-only mode teams)
  const allPlayers = [
    ...(homeTeam.teamOnlyMode ? [] : homeTeam.players.map((p) => ({ ...p, team: homeTeam }))),
    ...(awayTeam.teamOnlyMode ? [] : awayTeam.players.map((p) => ({ ...p, team: awayTeam }))),
  ].sort((a, b) => b.points - a.points);

  // Check if both teams are in team-only mode
  const bothTeamsOnly = homeTeam.teamOnlyMode && awayTeam.teamOnlyMode;

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden h-full flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <h3 className="font-semibold text-gray-700 uppercase text-sm tracking-wide">
          Top Scorers
        </h3>
      </div>

      {/* Player list */}
      <div className="flex-1 overflow-y-auto">
        {bothTeamsOnly ? (
          <div className="p-6 text-center text-gray-400 text-sm">
            <div className="mb-2">Team-Only Tracking Mode</div>
            <div className="text-xs">Individual player stats not available</div>
          </div>
        ) : (
          <>
            {allPlayers.filter(p => p.points > 0).slice(0, 10).map((player) => {
              const teamStyle = getTeamColorStyle(player.team.primaryColor);
              return (
                <div
                  key={player.id}
                  className="flex items-center gap-3 px-4 py-2 border-b border-gray-100 hover:bg-gray-50"
                >
                  {/* Jersey number with team color */}
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{
                      backgroundColor: teamStyle.backgroundColor,
                      color: teamStyle.color,
                      border: teamStyle.border,
                    }}
                  >
                    {player.jerseyNumber}
                  </div>

                  {/* Player info */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">
                      {player.firstName} {player.lastName}
                    </div>
                    <div className="text-xs text-gray-400">
                      {player.team.abbreviation}
                    </div>
                  </div>

                  {/* Fouls indicator */}
                  {player.fouls > 0 && (
                    <div
                      className={`px-2 py-0.5 rounded text-xs font-medium ${
                        player.fouls >= 5
                          ? 'bg-red-100 text-red-700'
                          : player.fouls >= 4
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {player.fouls}F
                    </div>
                  )}

                  {/* Points */}
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-800">{player.points}</div>
                    <div className="text-xs text-gray-400">pts</div>
                  </div>
                </div>
              );
            })}

            {allPlayers.filter(p => p.points > 0).length === 0 && (
              <div className="p-6 text-center text-gray-400 text-sm">
                No scoring yet
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
